/**
 * Resurs financing settings (#1457).
 *
 * Minimal per-workshop configuration: on/off, environment, store id, the
 * minimum amount for offering financing, and the client id / client secret
 * the workshop got from Resurs. The two credentials are write-only: the API
 * only says whether they are set, and an empty field keeps the stored value.
 * The integration starts off and on Resurs' integration environment; picking
 * production is an explicit choice here. Nothing on this tab moves money.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { ApiError } from '@/lib/api';
import { describeApiError } from '@/lib/describeApiError';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import { eurosToCents, formatCentsAsEuros, normalizeEuroInput } from '@/utils/money';
import {
  useDeleteResursCredentials,
  useResursIntegration,
  useSaveResursConfig,
  useTestResursConnection,
  type ResursConfig,
  type ResursEnvironment,
} from './useResursIntegration';

const STORE_ID_PATTERN = /^[A-Za-z0-9._:-]{1,100}$/;
const EURO_AMOUNT_PATTERN = /^\d{1,7}([.,]\d{1,2})?$/;

const ERROR_KEYS: Record<string, string> = {
  RESURS_ENCRYPTION_KEY_MISSING: 'errors.encryptionMissing',
  RESURS_CONFIG_INCOMPLETE: 'errors.configIncomplete',
  RESURS_CREDENTIALS_MISSING: 'errors.credentialsMissing',
};

const LAST_TEST_ERROR_KEYS: Record<string, string> = {
  RESURS_UNAUTHORIZED: 'errors.testUnauthorized',
  RESURS_REJECTED: 'errors.testRejected',
  RESURS_HTTP: 'errors.testHttp',
  RESURS_TIMEOUT: 'errors.testTimeout',
  RESURS_NETWORK: 'errors.testNetwork',
  RESURS_REDIRECT: 'errors.testMalformed',
  RESURS_MALFORMED: 'errors.testMalformed',
  RESURS_TOO_LARGE: 'errors.testMalformed',
  RESURS_BLOCKED: 'errors.testBlocked',
  RESURS_CONFIG: 'errors.credentialsMissing',
  RESURS_FAILED: 'errors.testFailed',
};

function lastTestErrorKey(error: string | undefined): string {
  return (error !== undefined ? LAST_TEST_ERROR_KEYS[error] : undefined) ?? 'errors.testFailed';
}

function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

function statusChip(config: ResursConfig): { color: 'success' | 'warning' | 'error' | 'default'; labelKey: string } {
  if (config.lastTest?.success === false) return { color: 'error', labelKey: 'statusFailed' };
  if (config.enabled && config.lastTest?.success === true) return { color: 'success', labelKey: 'statusReady' };
  if (config.enabled) return { color: 'warning', labelKey: 'statusUntested' };
  if (config.hasClientId || config.hasClientSecret) return { color: 'default', labelKey: 'statusDisabled' };
  return { color: 'default', labelKey: 'statusNotConfigured' };
}

export function ResursSettingsTab() {
  const { t } = useTranslation('resurs');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const query = useResursIntegration(true);
  const save = useSaveResursConfig();
  const test = useTestResursConnection();
  const remove = useDeleteResursCredentials();

  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState<ResursEnvironment>('integration');
  const [storeId, setStoreId] = useState('');
  const [minAmount, setMinAmount] = useState('0.00');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const config = query.data;
  useEffect(() => {
    if (!config) return;
    setEnabled(config.enabled);
    setEnvironment(config.environment);
    setStoreId(config.storeId ?? '');
    setMinAmount(formatCentsAsEuros(config.minAmountCents));
    setClientId('');
    setClientSecret('');
  }, [config]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={tc('loading')} />
      </Box>
    );
  }
  if (query.isError || !config) {
    return <Alert severity="error">{t('loadFailed')}</Alert>;
  }

  const showError = (error: unknown, fallbackKey: string) => {
    const known = ERROR_KEYS[errorCode(error) ?? ''];
    notify.error(tc('error'), known ? t(known) : describeApiError(error, tc, { fallback: t(fallbackKey) }));
  };

  const storeTrimmed = storeId.trim();
  const storeInvalid = storeTrimmed !== '' && !STORE_ID_PATTERN.test(storeTrimmed);
  const minAmountInvalid = !EURO_AMOUNT_PATTERN.test(minAmount.trim());
  const minAmountCents = minAmountInvalid ? null : eurosToCents(normalizeEuroInput(minAmount));

  // Another environment has other credentials: the server drops the stored ones.
  const environmentChanging = environment !== config.environment;
  const idSet = clientId.trim() !== '' || (config.hasClientId && !environmentChanging);
  const secretSet = clientSecret.trim() !== '' || (config.hasClientSecret && !environmentChanging);
  const incompleteForEnable = enabled && !(idSet && secretSet && storeTrimmed !== '');

  const canSave =
    !save.isPending &&
    !storeInvalid &&
    minAmountCents !== null &&
    !incompleteForEnable &&
    (config.encryptionAvailable || (clientId.trim() === '' && clientSecret.trim() === ''));

  const handleSave = async () => {
    if (!canSave || minAmountCents === null) return;
    try {
      await save.mutateAsync({
        enabled,
        environment,
        storeId: storeTrimmed === '' ? null : storeTrimmed,
        minAmountCents,
        ...(clientId.trim() !== '' ? { clientId: clientId.trim() } : {}),
        ...(clientSecret.trim() !== '' ? { clientSecret: clientSecret.trim() } : {}),
      });
      setClientId('');
      setClientSecret('');
      notify.success(t('saved'), t('savedDesc'));
    } catch (error) {
      showError(error, 'errors.saveFailed');
    }
  };

  const canTest =
    config.encryptionAvailable &&
    config.hasClientId &&
    config.hasClientSecret &&
    !test.isPending &&
    !save.isPending;

  const handleTest = async () => {
    if (!canTest) return;
    try {
      const result = await test.mutateAsync(undefined);
      if (result.success) {
        notify.success(t('testSuccess'), t('testSuccessDesc'));
      } else {
        notify.error(tc('error'), t(lastTestErrorKey(result.config.lastTest?.error)));
      }
    } catch (error) {
      showError(error, 'errors.testFailed');
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(undefined);
      setConfirmDelete(false);
      notify.success(t('deleted'), t('deletedDesc'));
    } catch (error) {
      showError(error, 'errors.deleteFailed');
    }
  };

  const chip = statusChip(config);
  const lastTest = config.lastTest;

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="h6">{t('title')}</Typography>
          <Chip size="small" color={chip.color} label={t(chip.labelKey)} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('intro')}
        </Typography>
      </Box>

      {!config.encryptionAvailable && <Alert severity="warning">{t('encryptionMissing')}</Alert>}
      {environment === 'production' && <Alert severity="warning">{t('productionWarning')}</Alert>}
      {lastTest?.success === false && <Alert severity="error">{t(lastTestErrorKey(lastTest.error))}</Alert>}

      <Stack spacing={2}>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={t('enabled')}
        />
        {incompleteForEnable && <Alert severity="info">{t('enableNeedsConfig')}</Alert>}
        <TextField
          select
          label={t('environment')}
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as ResursEnvironment)}
          helperText={
            environmentChanging && (config.hasClientId || config.hasClientSecret)
              ? t('environmentChangeWarning')
              : t('environmentHint')
          }
          fullWidth
        >
          <MenuItem value="integration">{t('environmentIntegration')}</MenuItem>
          <MenuItem value="production">{t('environmentProduction')}</MenuItem>
        </TextField>
        <TextField
          label={t('storeId')}
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          error={storeInvalid}
          helperText={storeInvalid ? t('storeIdInvalid') : t('storeIdHint')}
          autoComplete="off"
          inputProps={{ maxLength: 100 }}
          fullWidth
        />
        <TextField
          label={t('minAmount')}
          value={minAmount}
          onChange={(e) => setMinAmount(e.target.value)}
          error={minAmountInvalid}
          helperText={minAmountInvalid ? t('minAmountInvalid') : t('minAmountHint')}
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
          fullWidth
        />
        <TextField
          label={t('clientId')}
          type="password"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          helperText={config.hasClientId ? t('credentialSetHint') : t('clientIdHint')}
          autoComplete="off"
          fullWidth
        />
        <TextField
          label={t('clientSecret')}
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          helperText={config.hasClientSecret ? t('credentialSetHint') : t('clientSecretHint')}
          autoComplete="new-password"
          fullWidth
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {t('save')}
          </Button>
          <Button
            variant="outlined"
            startIcon={test.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => void handleTest()}
            disabled={!canTest}
          >
            {t('test')}
          </Button>
          <Button
            color="error"
            onClick={() => setConfirmDelete(true)}
            disabled={!(config.hasClientId || config.hasClientSecret) || remove.isPending}
          >
            {t('delete')}
          </Button>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={confirmDelete}
        title={t('deleteTitle')}
        message={t('deleteMessage')}
        confirmColor="error"
        confirmText={tc('delete')}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
        loading={remove.isPending}
      />
    </Stack>
  );
}
